// src/services/adhocMealPlans.ts
//
// Adhoc-meal-plan-domain service. Backs the Adhoc meal plans dashboard
// (list + filter + search + pagination).

import { apiFetch } from "../Component/api/apiFetch";
import { API_URLS } from "../Component/api/url";
import { handleJson } from "./http";

export const createAdhocMealPlanFromScratch = (
  payload: Record<string, unknown>,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.CREATE_ADHOC_MEAL_PLAN_FORM_SCRATCH, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  );

export const createAdhocMealPlanFromTemplate = (
  payload: Record<string, unknown>,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.CREATE_ADHOC_MEAL_PLAN_FROM_TEMPLATE, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  );

export const createAdhocMealPlanFromExistingClientPlan = (
  payload: Record<string, unknown>,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.CREATE_ADHOC_MEAL_PLAN_FROM_EXISTING_CLIENT_PLAN, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  );

export const duplicateAdhocMealPlan = (
  payload: {
    source_plan_id: number;
    first_name: string;
    last_name: string;
    contact_number: string;
    client_email: string;
    location: string;
    category_id: number;
    plan_name: string;
    meal_plan_type: string;
    plan_goal: string;
    total_calories_min: string | number;
    total_calories_max: string | number;
    total_protein_min: string | number;
    total_protein_max: string | number;
    total_carbs_min: string | number;
    total_carbs_max: string | number;
    total_fats_min: string | number;
    total_fats_max: string | number;
    total_GL_min: string | number;
    total_GL_max: string | number;
    CPF_min: string;
    CPF_max: string;
    instructions: string;
  },
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.DUPLICATE_ADHOC_MEAL_PLAN, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  );

export const addAdhocMealPlanOption = (
  adhocPlanId: string | number,
  payload: Record<string, unknown>,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.ADD_ADHOC_MEAL_PLAN_OPTIONS(adhocPlanId), {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  );

export const updateAdhocMealPlanOption = (
  adhocPlanId: string | number,
  payload: Record<string, unknown>,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.UPDATE_ADHOC_MEAL_PLAN_OPTIONS(adhocPlanId), {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  );

export const deleteAdhocMealPlanOption = (
  adhocPlanId: string | number,
  payload: Record<string, unknown>,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.DELETE_ADHOC_MEAL_PLAN_OPTIONS(adhocPlanId), {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  );

export const renameAdhocMealPlan = (
  adhocPlanId: string | number,
  payload: Record<string, unknown>,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.RENAME_ADHOC_MEAL_PLAN(adhocPlanId), {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  );

// Single endpoint handles three variants based on payload shape:
//   Copy   → { adhoc_plan_id, source_option_id, new_option_name }
//   Rename → adds { target_meal_id: <same as source> }
//   Move   → adds { target_meal_id: <different meal> }
export const duplicateAdhocMealPlanOption = (
  payload: Record<string, unknown>,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.DUPLICATE_ADHOC_MEAL_PLAN_OPTIONS, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  );

// Single endpoint handles update / add / delete for meal timings via the
// `meal_updates` array. Each entry:
//   Update → { meal_id, fk_adhoc_plan, hour, minute, period, meal_type_name }
//   Add    → same shape with meal_id: 0
//   Delete → { meal_id, delete: true }
export const updateAdhocMealPlanMealTime = (
  payload: Record<string, unknown>,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.UPDATE_ADHOC_MEAL_PLAN_MEAL_TIME, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  );

export const publishAdhocMealPlan = (
  adhocPlanId: string | number,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.PUBLISH_ADHOC_MEAL_PLAN(adhocPlanId), {
      method: "POST",
    }),
  );

// Poll-based download endpoint. Backend returns EITHER:
//   - application/json with { success:"1", data:{ status:"generating" } } while the
//     PDF is still being generated (caller should poll again), or
//   - application/pdf with the actual PDF bytes once ready.
// Returning the raw Response lets the caller inspect Content-Type before
// deciding whether to parse JSON or download the blob.
export const downloadAdhocMealPlan = (
  adhocPlanId: string | number,
): Promise<Response> =>
  apiFetch(API_URLS.DOWNLOAD_ADHOC_MEAL_PLAN(adhocPlanId), {
    method: "GET",
  });

export const getAdhocMealPlanDetails = (
  adhocPlanId: string | number,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_ADHOC_MEAL_PLAN_DETAILS(adhocPlanId), {
      method: "GET",
    }),
  );

export const editAdhocMealPlanNutrientValues = (
  adhocPlanId: string | number,
  payload: Record<string, unknown>,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.EDIT_ADHOC_MEAL_PLAN_NUTRIENT_VALUES(adhocPlanId), {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  );

export const assignAdhocMealPlanToClient = (
  payload: { adhoc_plan_id: number; target_user_id: number },
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.ASSIGN_ADHOC_MEAL_PLAN_TO_CLIENT, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  );

export const archiveAdhocMealPlan = (
  adhocPlanId: string | number,
  reason: string,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.ARCHIVE_ADHOC_MEAL_PLAN(adhocPlanId), {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  );

// Restore defaults archived plans back to "draft" (per backend spec). The
// modal captures a reason for parity with the archive flow, so it rides along
// in the body even though the backend only documents `status`.
export const restoreAdhocMealPlan = (
  adhocPlanId: string | number,
  reason: string,
  status: "draft" | "active" = "draft",
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.RESTORE_ADHOC_MEAL_PLAN(adhocPlanId), {
      method: "POST",
      body: JSON.stringify({ status, reason }),
    }),
  );

// Hard-deletes a draft adhoc plan. Backend only permits this for draft-status
// plans; non-draft rows must go through archive/restore instead.
export const deleteAdhocMealPlan = (
  adhocPlanId: string | number,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.DELETE_ADHOC_MEAL_PLAN(adhocPlanId), {
      method: "DELETE",
    }),
  );

export const getAdhocMealPlanCategories = (): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_ADHOC_MEAL_PLAN_CATEGORIES, { method: "GET" }),
  );

export const getAdhocMealPlanList = (
  search: string = "",
  status: string = "",
  planType: string = "",
  categoryId: string | number = "",
  nutritionistId: string | number = "",
  createdFrom: string = "",
  createdTo: string = "",
  sortBy: string = "",
  sortOrder: string = "",
  page: number = 1,
  pageSize: number = 20,
): Promise<any> =>
  handleJson(
    apiFetch(
      API_URLS.GET_ALL_ADHOC_MEAL_PLAN_LIST(
        search,
        status,
        planType,
        categoryId,
        nutritionistId,
        createdFrom,
        createdTo,
        sortBy,
        sortOrder,
        page,
        pageSize,
      ),
      { method: "GET" },
    ),
  );
